'use client'

import Modal from '@/components/Modal/Modal'
import { ObjectDisplay } from '@/components/WorkflowToolResults/shared/ObjectDisplay'
import { WorkflowToolResult } from '@/components/WorkflowToolResults/WorkflowToolResult'
import {
  UpdateScheduledTaskInput,
  useGetScheduledTaskQuery,
  useGetScheduledTaskResultsQuery,
  useMarkResultsSeenMutation,
  useRunNowScheduledTaskMutation,
  useSkipNextScheduledTaskMutation,
  useUpdateScheduledTaskMutation,
  type ScheduledTaskToolResult,
} from '@/lib/features/scheduled-tasks/scheduledTasksApi'
import {
  extractWorkflowExecutionId,
  useOpenScheduledTaskInCopilot,
} from '@/lib/features/scheduled-tasks/useOpenScheduledTaskInCopilot'
import { useGetUserQuery } from '@/lib/features/user/userApi'
import { cn } from '@/lib/utils'
import { filterNodeExecutionsForOutputs } from '@/lib/utils/workflowFilterUtils'
import { getToolDisplay } from '@/utils/copilotToolMappings'
import { AnimatePresence, motion } from 'framer-motion'
import {
  Bell,
  Bug,
  Calendar,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Clock,
  Edit2,
  Info,
  Loader2,
  MessageCircle,
  Pause,
  Pencil,
  Play,
  Star,
  Trash2,
  X,
  XCircle,
} from 'lucide-react'

import { useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { TemplateToolIdValues } from 'sprites-types/api/v2/copilot-tools'
import type { NodeExecution, WorkflowExecutionResponse } from 'sprites-types/api/v2/workflows'
import type { WorkflowConfig } from 'sprites-types/workflow/core'
import {
  ScheduleEditor,
  buildRepeatSpec,
  formatHourForDisplay,
  getDisplayMinuteForTimezone,
  getFirstRunAtISO,
  getOrdinalSuffix,
  getShortTimezoneAbbr,
  getUserTimezone,
  repeatSpecToEditorState,
  utcHourAndDaysToLocal,
  type IntervalUnit,
  type MonthWeekdayRule,
  type SchedulePreset,
} from './ScheduleEditor'
import { ToolChip } from './ToolChip'

interface ScheduledTaskModalProps {
  taskId: string
  isOpen: boolean
  onClose: () => void
  onUpdate: (id: string, input: UpdateScheduledTaskInput) => Promise<void>
  onDelete: (id: string) => Promise<void>
  initialExecutionId?: string
}

const statusColors = {
  PENDING: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  RUNNING: 'bg-blue-100 text-blue-800 border-blue-200',
  COMPLETED: 'bg-green-100 text-green-800 border-green-200',
  FAILED: 'bg-red-100 text-red-800 border-red-200',
  CANCELLED: 'bg-gray-100 text-gray-800 border-gray-200',
  PAUSED: 'bg-orange-100 text-orange-800 border-orange-200',
}

function formatDate(date: Date | string | null | undefined, timezone?: string | null): string {
  if (!date) return 'Never'
  try {
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      ...(timezone ? { timeZone: timezone } : {}),
    })
  } catch {
    return String(date)
  }
}

const weekDays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

function formatDuration(ms: number | null): string {
  if (ms === null) return '-'
  if (ms < 1000) return `${ms}ms`
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`
  return `${(ms / 60000).toFixed(1)}m`
}

function ResultStatusIcon({ status }: { status: ScheduledTaskToolResult['status'] }) {
  switch (status) {
    case 'COMPLETED':
      return <CheckCircle2 className="h-4 w-4 text-green-500" />
    case 'FAILED':
      return <XCircle className="h-4 w-4 text-red-500" />
    case 'RUNNING':
      return <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
  }
}

// Direct workflow execution tools
const DIRECT_WORKFLOW_TOOLS = ['executeWorkflow', 'preExecuteWorkflow', 'rerunFromNodeExecution']

// Template tools also execute workflows internally (e.g. facebookAdsInsights, seoArticleWriter)
const TEMPLATE_TOOL_IDS = new Set<string>(Object.values(TemplateToolIdValues))

function isWorkflowExecutionResult(toolName: string): boolean {
  return DIRECT_WORKFLOW_TOOLS.includes(toolName) || TEMPLATE_TOOL_IDS.has(toolName)
}

/**
 * Fetches workflow execution data and renders the actual WorkflowToolResult
 * components -- the same rich per-tool views used in the copilot chat.
 */
function WorkflowResultView({ executionId }: { executionId: string }) {
  const [nodeExecutions, setNodeExecutions] = useState<Record<string, NodeExecution> | null>(null)
  const [workflowConfig, setWorkflowConfig] = useState<WorkflowConfig | undefined>(undefined)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    const fetchData = async () => {
      try {
        const response = await fetch(
          `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v2/workflows/execution/${executionId}`,
          { method: 'GET', credentials: 'include' }
        )
        if (!response.ok) {
          throw new Error(`Failed to fetch execution data: ${response.status}`)
        }

        const data = (await response.json()) as WorkflowExecutionResponse
        if (cancelled) return

        setNodeExecutions(data.nodeExecutions)

        // Fetch workflow config for filtering output nodes
        if (data.executionData.workflowRef) {
          const wfResponse = await fetch(
            `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v2/workflows/${data.executionData.workflowRef}`,
            { method: 'GET', credentials: 'include' }
          )
          if (wfResponse.ok) {
            const wfData = await wfResponse.json()
            if (!cancelled && wfData.success && wfData.workflow?.data) {
              setWorkflowConfig(wfData.workflow.data as WorkflowConfig)
            }
          }
        }

        if (!cancelled) setIsLoading(false)
      } catch (err) {
        if (!cancelled) {
          console.error('[WorkflowResultView] Failed to fetch execution data:', err)
          setError(err instanceof Error ? err.message : 'Failed to load results')
          setIsLoading(false)
        }
      }
    }

    void fetchData()
    return () => {
      cancelled = true
    }
  }, [executionId])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-4">
        <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
        <span className="text-xs text-gray-500">Loading workflow results...</span>
      </div>
    )
  }

  if (error || !nodeExecutions) {
    return (
      <div className="rounded-lg bg-red-50 p-2">
        <p className="text-xs text-red-600">{error ?? 'No execution data available'}</p>
      </div>
    )
  }

  const execArray = Object.values(nodeExecutions)

  // Filter to output nodes if workflow config is available
  const outputFiltered = workflowConfig
    ? filterNodeExecutionsForOutputs(execArray, workflowConfig)
    : execArray

  // Exclude failed/errored
  const filtered = outputFiltered.filter(
    (exec) => exec.status !== 'failed' && exec.status !== 'errored'
  )

  if (filtered.length === 0) {
    return (
      <div className="rounded-lg bg-gray-50 p-2 text-center">
        <p className="text-xs text-gray-500">No output results to display</p>
      </div>
    )
  }

  // Provide a minimal fallback workflow config for WorkflowToolResult
  const config: WorkflowConfig = workflowConfig ?? {
    id: 'unknown',
    name: 'Unknown Workflow',
    description: '',
    start: '',
    nodes: [],
    parameters: { type: 'object', properties: {}, required: [] },
    outputs: [],
  }

  return (
    <div className="flex flex-col gap-y-3">
      {filtered.map((nodeExecution) => (
        <WorkflowToolResult
          key={nodeExecution.executionId}
          nodeExecution={nodeExecution}
          workflowConfig={config}
        />
      ))}
    </div>
  )
}

export function ScheduledTaskModal({
  taskId,
  isOpen,
  onClose,
  onUpdate,
  onDelete,
  initialExecutionId,
}: ScheduledTaskModalProps) {
  const { data: task, isLoading } = useGetScheduledTaskQuery(taskId, { skip: !isOpen })
  const { data: resultsData, isLoading: isLoadingResults } = useGetScheduledTaskResultsQuery(
    { taskId, limit: 10 },
    { skip: !isOpen }
  )
  const [updateTask] = useUpdateScheduledTaskMutation()
  const [skipNextTask] = useSkipNextScheduledTaskMutation()
  const [runNowTask, { isLoading: isRunningNow }] = useRunNowScheduledTaskMutation()
  const [markSeen] = useMarkResultsSeenMutation()
  const { data: userData } = useGetUserQuery()
  const workspaceId = userData?.workspace?.id
  const { openExecution } = useOpenScheduledTaskInCopilot()
  const [creatingSessionForId, setCreatingSessionForId] = useState<string | null>(null)
  const [isEditingSchedule, setIsEditingSchedule] = useState(false)
  const [showResults, setShowResults] = useState(false)
  const [resultModalResult, setResultModalResult] = useState<ScheduledTaskToolResult | null>(null)
  const [resultModalDebugMode, setResultModalDebugMode] = useState(false)
  const [openToolIndex, setOpenToolIndex] = useState<number | null>(null)
  const [isEditingName, setIsEditingName] = useState(false)
  const [isEditingDescription, setIsEditingDescription] = useState(false)
  const [editName, setEditName] = useState('')
  const [editDescription, setEditDescription] = useState('')
  const [isSavingName, setIsSavingName] = useState(false)
  const [isSavingDescription, setIsSavingDescription] = useState(false)
  const nameInputRef = useRef<HTMLInputElement>(null)
  const descriptionTextareaRef = useRef<HTMLTextAreaElement>(null)
  const autoExpandHandledRef = useRef(false)

  // Schedule editing state - using new unified model
  const [editStartDate, setEditStartDate] = useState<string>('')
  const [editStartHour, setEditStartHour] = useState<number>(9)
  const [editPreset, setEditPreset] = useState<SchedulePreset>('daily')
  const [editInterval, setEditInterval] = useState<number>(1)
  const [editIntervalUnit, setEditIntervalUnit] = useState<IntervalUnit>('day')
  const [editDaysOfWeek, setEditDaysOfWeek] = useState<number[]>([])
  const [editMonthDayOfMonth, setEditMonthDayOfMonth] = useState<number | null>(null)
  const [editMonthWeekdayRule, setEditMonthWeekdayRule] = useState<MonthWeekdayRule | null>(null)
  const [editMaxRuns, setEditMaxRuns] = useState<number | null>(null)
  const [editEndMode, setEditEndMode] = useState<'none' | 'maxRuns' | 'endDate'>('none')
  const [editEndDate, setEditEndDate] = useState<string>('')
  const [editTimezone, setEditTimezone] = useState<string>(() => getUserTimezone())

  useEffect(() => {
    if (task && isEditingSchedule) {
      const tz = task.timezone ?? getUserTimezone()
      const editorState = repeatSpecToEditorState(task.repeatSpec ?? null, tz)
      setEditPreset(editorState.preset)
      setEditInterval(editorState.interval)
      setEditIntervalUnit(editorState.intervalUnit)
      setEditStartHour(editorState.startHour)
      setEditDaysOfWeek(editorState.daysOfWeek)
      setEditMonthDayOfMonth(editorState.monthDayOfMonth)
      setEditMonthWeekdayRule(editorState.monthWeekdayRule)
      setEditMaxRuns(task.maxRuns ?? null)
      setEditEndMode(task.maxRuns ? 'maxRuns' : 'none')
      setEditTimezone(tz)
      // Use the task's nextRunAt as start date if available, otherwise default to tomorrow
      if (task.nextRunAt) {
        const nextRun = new Date(task.nextRunAt)
        // If nextRunAt is in the past, use tomorrow instead
        const now = new Date()
        if (nextRun > now) {
          setEditStartDate(nextRun.toISOString().split('T')[0])
        } else {
          const tomorrow = new Date()
          tomorrow.setDate(tomorrow.getDate() + 1)
          setEditStartDate(tomorrow.toISOString().split('T')[0])
        }
      } else {
        const tomorrow = new Date()
        tomorrow.setDate(tomorrow.getDate() + 1)
        setEditStartDate(tomorrow.toISOString().split('T')[0])
      }
    }
  }, [task, isEditingSchedule])

  // Initialize name and description when task loads
  useEffect(() => {
    if (task) {
      setEditName(task.name)
      setEditDescription(task.description || '')
    }
  }, [task])

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditingName && nameInputRef.current) {
      nameInputRef.current.focus()
      nameInputRef.current.select()
    }
  }, [isEditingName])

  useEffect(() => {
    if (isEditingDescription && descriptionTextareaRef.current) {
      descriptionTextareaRef.current.focus()
      descriptionTextareaRef.current.setSelectionRange(
        descriptionTextareaRef.current.value.length,
        descriptionTextareaRef.current.value.length
      )
    }
  }, [isEditingDescription])

  // Auto-expand results when deep-linked via email
  useEffect(() => {
    if (!initialExecutionId || isLoadingResults || !resultsData || autoExpandHandledRef.current)
      return
    setShowResults(true)
    autoExpandHandledRef.current = true
  }, [initialExecutionId, isLoadingResults, resultsData])

  if (isLoading || !task) {
    return (
      <Modal isOpen={isOpen} onClose={onClose}>
        <div className="max-w-3xl rounded-3xl bg-white p-8">
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="mb-4 text-gray-400">Loading task details...</div>
            </div>
          </div>
        </div>
      </Modal>
    )
  }

  const handleSaveName = async () => {
    const trimmed = editName.trim()
    if (!trimmed) {
      setEditName(task.name)
      setIsEditingName(false)
      return
    }
    if (trimmed === task.name) {
      setIsEditingName(false)
      return
    }

    setIsSavingName(true)
    try {
      await onUpdate(task.id, {
        name: trimmed,
      })
      setIsEditingName(false)
    } catch {
      setEditName(task.name)
    } finally {
      setIsSavingName(false)
    }
  }

  const handleSaveDescription = async () => {
    const trimmed = editDescription.trim()
    const currentDescription = task.description || ''
    if (trimmed === currentDescription) {
      setIsEditingDescription(false)
      return
    }

    setIsSavingDescription(true)
    try {
      await onUpdate(task.id, {
        description: trimmed || null,
      })
      setIsEditingDescription(false)
    } catch {
      setEditDescription(task.description || '')
    } finally {
      setIsSavingDescription(false)
    }
  }

  const handleNameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSaveName()
    } else if (e.key === 'Escape') {
      setEditName(task.name)
      setIsEditingName(false)
    }
  }

  const handleDescriptionKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setEditDescription(task.description || '')
      setIsEditingDescription(false)
    }
  }

  const handleSaveSchedule = async () => {
    try {
      let resolvedMaxRuns: number | null = null

      if (editEndMode === 'maxRuns' && editMaxRuns && editMaxRuns > 0) {
        resolvedMaxRuns = editMaxRuns
      } else if (editEndMode === 'endDate' && editEndDate && editStartDate) {
        // Calculate max runs from end date
        const start = new Date(editStartDate)
        const end = new Date(editEndDate)

        if (end < start) {
          toast.error('End date must be on or after the start date.')
          return
        }

        const startMidnight = new Date(start.getFullYear(), start.getMonth(), start.getDate())
        const endMidnight = new Date(end.getFullYear(), end.getMonth(), end.getDate())
        const diffDays = Math.floor(
          (endMidnight.getTime() - startMidnight.getTime()) / (1000 * 60 * 60 * 24)
        )

        if (editPreset === 'never') {
          resolvedMaxRuns = endMidnight >= startMidnight ? 1 : 0
        } else if (
          editPreset === 'daily' ||
          (editPreset === 'custom' && editIntervalUnit === 'day' && editInterval === 1)
        ) {
          resolvedMaxRuns = diffDays + 1
        } else if (editPreset === 'custom' && editIntervalUnit === 'day') {
          resolvedMaxRuns = Math.floor(diffDays / editInterval) + 1
        } else if (
          editPreset === 'weekly' ||
          (editPreset === 'custom' && editIntervalUnit === 'week')
        ) {
          const daySet = new Set(
            editDaysOfWeek.length > 0 ? editDaysOfWeek : [new Date(editStartDate).getDay()]
          )
          const weekInterval = editPreset === 'weekly' ? 1 : editInterval
          let count = 0
          const cursor = new Date(startMidnight)
          while (cursor <= endMidnight) {
            if (daySet.has(cursor.getDay())) {
              const daysSinceStart = Math.round(
                (cursor.getTime() - startMidnight.getTime()) / (24 * 60 * 60 * 1000)
              )
              const weeksSinceStart = Math.floor(daysSinceStart / 7)
              if (weeksSinceStart % weekInterval === 0) count += 1
            }
            cursor.setDate(cursor.getDate() + 1)
          }
          resolvedMaxRuns = count
        } else if (editPreset === 'custom' && editIntervalUnit === 'month') {
          let count = 0
          const cursor = new Date(startMidnight)
          const [year, month, day] = editStartDate.split('-').map(Number)
          const targetDay = new Date(year, month - 1, day).getDate()
          const daysInMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate()
          while (cursor <= endMidnight) {
            count += 1
            cursor.setMonth(cursor.getMonth() + editInterval)
            cursor.setDate(Math.min(targetDay, daysInMonth(cursor)))
          }
          resolvedMaxRuns = count
        }

        if (resolvedMaxRuns !== null && resolvedMaxRuns <= 0) {
          toast.error('End date does not allow any runs with the selected schedule.')
          return
        }
      }

      const editRepeatSpec = buildRepeatSpec({
        preset: editPreset,
        startHour: editStartHour,
        interval: editInterval,
        intervalUnit: editIntervalUnit,
        daysOfWeek: editDaysOfWeek,
        monthDayOfMonth: editMonthDayOfMonth,
        monthWeekdayRule: editMonthWeekdayRule,
        timezone: editTimezone || undefined,
      })

      const firstRunAt =
        editStartDate !== ''
          ? getFirstRunAtISO(editStartDate, editStartHour, editTimezone || getUserTimezone())
          : null

      await onUpdate(task.id, {
        repeatSpec: editRepeatSpec,
        maxRuns: resolvedMaxRuns,
        timezone: editTimezone || null,
        ...(firstRunAt !== null && { firstRunAt }),
      })
      setIsEditingSchedule(false)
      toast.success('Schedule updated successfully')
    } catch {
      // Error handled by onUpdate
    }
  }

  const handleDelete = async () => {
    if (confirm('Delete this scheduled task? This cannot be undone.')) {
      await onDelete(task.id)
      onClose()
    }
  }

  const handleOpenExecutionInCopilot = async (results: ScheduledTaskToolResult[]) => {
    if (!workspaceId) {
      toast.error('Unable to create session')
      return
    }

    setCreatingSessionForId(results[0].executionId)
    try {
      await openExecution(results[0].executionId, {
        workspaceId,
        results,
        taskName: task.name,
      })
    } catch {
      toast.error('Failed to open in Copilot')
    } finally {
      setCreatingSessionForId(null)
    }
  }

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} className="w-full max-w-4xl">
        <div className="font-space-grotesk flex max-h-[90vh] w-full min-w-0 flex-col overflow-y-auto rounded-3xl bg-white p-8 shadow-2xl">
          <div className="mb-6 flex items-start justify-between">
            <div className="flex-1">
              <div className="mb-2 flex items-center gap-3">
                {isEditingName ? (
                  <input
                    ref={nameInputRef}
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    onBlur={handleSaveName}
                    onKeyDown={handleNameKeyDown}
                    onClick={(e) => e.stopPropagation()}
                    disabled={isSavingName}
                    className={cn(
                      'min-w-0 flex-1 rounded border border-gray-300 bg-white px-2 py-1 text-2xl font-semibold text-gray-900 focus:border-gray-500 focus:outline-none',
                      isSavingName && 'cursor-wait opacity-50'
                    )}
                  />
                ) : (
                  <div className="group flex min-w-0 flex-1 items-center gap-2">
                    <h2 className="line-clamp-1 min-w-0 flex-1 text-2xl font-semibold text-gray-900">
                      {task.name}
                    </h2>
                    <motion.button
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        setIsEditingName(true)
                      }}
                      className="flex-shrink-0 rounded p-1 text-gray-400 opacity-0 transition-opacity hover:bg-gray-100 hover:text-gray-600 group-hover:opacity-100"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                      aria-label="Edit name"
                    >
                      <Pencil className="h-4 w-4" />
                    </motion.button>
                  </div>
                )}
                <span
                  className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-medium ${statusColors[task.status]}`}
                >
                  {task.status === 'PENDING'
                    ? 'Scheduled'
                    : task.status === 'PAUSED'
                      ? 'Frozen'
                      : task.status === 'CANCELLED'
                        ? 'Skipped'
                        : task.status}
                </span>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    void onUpdate(task.id, { bookmark: !task.bookmark })
                  }}
                  className="flex-shrink-0 rounded-full p-1.5 text-gray-400 hover:text-yellow-500"
                  title={task.bookmark ? 'Remove from favourites' : 'Add to favourites'}
                >
                  <Star
                    size={18}
                    className={task.bookmark ? 'fill-yellow-400 text-yellow-400' : ''}
                  />
                </button>
              </div>
              {isEditingDescription ? (
                <textarea
                  ref={descriptionTextareaRef}
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  onBlur={handleSaveDescription}
                  onKeyDown={handleDescriptionKeyDown}
                  onClick={(e) => e.stopPropagation()}
                  disabled={isSavingDescription}
                  placeholder="Description (optional)"
                  rows={3}
                  className={cn(
                    'w-full rounded border border-gray-300 bg-white px-2 py-1 text-sm text-gray-900 focus:border-gray-500 focus:outline-none',
                    isSavingDescription && 'cursor-wait opacity-50'
                  )}
                />
              ) : (
                <div className="group flex min-w-0 items-start gap-2">
                  {task.description ? (
                    <p className="min-w-0 flex-1 text-sm text-gray-600">{task.description}</p>
                  ) : (
                    <p className="min-w-0 flex-1 text-sm italic text-gray-400">No description</p>
                  )}
                  <motion.button
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      setIsEditingDescription(true)
                    }}
                    className="mt-0.5 flex-shrink-0 rounded p-1 text-gray-400 opacity-0 transition-opacity hover:bg-gray-100 hover:text-gray-600 group-hover:opacity-100"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    aria-label="Edit description"
                  >
                    <Pencil className="h-3 w-3" />
                  </motion.button>
                </div>
              )}
            </div>
            <button
              onClick={onClose}
              className="ml-4 rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            >
              <X size={24} />
            </button>
          </div>

          {task.toolCalls.length > 0 && (
            <div className="mb-4 flex items-center gap-2">
              <span className="flex-shrink-0 text-xs text-gray-600">Tools:</span>
              <div className="flex min-w-0 flex-1 gap-2 overflow-x-auto overflow-y-visible pb-1">
                {task.toolCalls.map((tc, index) => {
                  const args =
                    typeof tc.function.arguments === 'string'
                      ? (JSON.parse(tc.function.arguments) as Record<string, unknown>)
                      : tc.function.arguments
                  return (
                    <ToolChip
                      key={index}
                      toolCall={{ function: { name: tc.function.name, arguments: args } }}
                      isOpen={openToolIndex === index}
                      onToggle={() => setOpenToolIndex(openToolIndex === index ? null : index)}
                    />
                  )
                })}
              </div>
            </div>
          )}

          <div className="mb-6 grid grid-cols-2 gap-4">
            <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
              <div className="mb-1 flex items-center gap-2 text-xs font-medium text-gray-500">
                <Clock size={14} />
                Next Run
              </div>
              <div className="text-sm font-semibold text-gray-900">
                {task.nextRunAt ? formatDate(task.nextRunAt, task.timezone) : 'Not scheduled'}
              </div>
            </div>

            <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
              <div className="mb-1 flex items-center gap-2 text-xs font-medium text-gray-500">
                <Calendar size={14} />
                Last Run
              </div>
              <div className="text-sm font-semibold text-gray-900">
                {task.lastRunAt ? formatDate(task.lastRunAt, task.timezone) : 'Never'}
              </div>
            </div>

            <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
              <div className="mb-1 flex items-center gap-2 text-xs font-medium text-gray-500">
                <Play size={14} />
                Runs Completed
              </div>
              <div className="text-sm font-semibold text-gray-900">
                {task.runCount}
                {task.maxRuns && ` of ${task.maxRuns}`}
              </div>
            </div>

            <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
              <div className="mb-1 flex items-center gap-2 text-xs font-medium text-gray-500">
                <Info size={14} />
                Created
              </div>
              <div className="text-sm font-semibold text-gray-900">
                {formatDate(task.createdAt)}
              </div>
            </div>
          </div>

          {/* Schedule Section */}
          <div className="mb-6 rounded-2xl border border-gray-200 bg-gray-50 p-4">
            <div className="mb-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Calendar size={16} className="text-gray-600" />
                <h3 className="text-sm font-medium text-gray-700">Schedule</h3>
              </div>
              {!isEditingSchedule && (
                <button
                  onClick={() => setIsEditingSchedule(true)}
                  className="flex items-center gap-1 rounded-xl px-3 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100"
                >
                  <Edit2 size={12} />
                  Edit
                </button>
              )}
            </div>

            {isEditingSchedule ? (
              <div>
                <ScheduleEditor
                  startDate={editStartDate}
                  startHour={editStartHour}
                  timezone={editTimezone}
                  preset={editPreset}
                  interval={editInterval}
                  intervalUnit={editIntervalUnit}
                  daysOfWeek={editDaysOfWeek}
                  monthDayOfMonth={editMonthDayOfMonth}
                  monthWeekdayRule={editMonthWeekdayRule}
                  maxRuns={editMaxRuns}
                  endMode={editEndMode}
                  endDate={editEndDate}
                  onStartDateChange={setEditStartDate}
                  onStartHourChange={setEditStartHour}
                  onTimezoneChange={setEditTimezone}
                  onPresetChange={setEditPreset}
                  onIntervalChange={setEditInterval}
                  onIntervalUnitChange={setEditIntervalUnit}
                  onDaysOfWeekChange={setEditDaysOfWeek}
                  onMonthDayOfMonthChange={setEditMonthDayOfMonth}
                  onMonthWeekdayRuleChange={setEditMonthWeekdayRule}
                  onMaxRunsChange={setEditMaxRuns}
                  onEndModeChange={setEditEndMode}
                  onEndDateChange={setEditEndDate}
                />
                <div className="mt-4 flex gap-2">
                  <button
                    onClick={handleSaveSchedule}
                    className="rounded-2xl bg-black px-4 py-2 text-sm font-medium text-white hover:bg-gray-800"
                  >
                    Save Schedule
                  </button>
                  <button
                    onClick={() => setIsEditingSchedule(false)}
                    className="rounded-2xl border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-sm text-gray-600">
                {task.repeatSpec
                  ? (() => {
                      const tz = task.timezone ?? getUserTimezone()
                      const state = repeatSpecToEditorState(task.repeatSpec, tz)
                      const displayMinute = getDisplayMinuteForTimezone(tz, new Date())
                      const timeLabel = formatHourForDisplay(state.startHour, displayMinute)
                      const tzAbbr = getShortTimezoneAbbr(tz)

                      // Shared time node with timezone abbreviation
                      const timeNode = (
                        <span className="font-semibold text-gray-900">
                          {timeLabel} {tzAbbr}
                        </span>
                      )

                      // Browser-local equivalent note when task timezone differs
                      const browserTz = getUserTimezone()
                      let browserNote: React.ReactNode = null
                      if (state.preset !== 'never' && tz !== browserTz) {
                        const utcHour = task.repeatSpec.startHour ?? 9
                        const { localHour: browserHour } = utcHourAndDaysToLocal(
                          utcHour,
                          [],
                          browserTz
                        )
                        const browserDisplayMinute = getDisplayMinuteForTimezone(
                          browserTz,
                          new Date()
                        )
                        const browserLabel = formatHourForDisplay(browserHour, browserDisplayMinute)
                        const browserAbbr = getShortTimezoneAbbr(browserTz)
                        browserNote = (
                          <div className="mt-0.5 text-xs text-gray-400">
                            {browserLabel} {browserAbbr} your time
                          </div>
                        )
                      }

                      if (state.preset === 'never') return 'Never'

                      let desc: React.ReactNode
                      if (state.preset === 'daily') {
                        desc = <>Every day at {timeNode}</>
                      } else if (state.preset === 'weekly') {
                        desc = (
                          <>
                            Every{' '}
                            <span className="font-semibold text-gray-900">
                              {state.daysOfWeek.length > 0
                                ? state.daysOfWeek.map((d) => weekDays[d]).join(', ')
                                : weekDays[0]}
                            </span>{' '}
                            at {timeNode}
                          </>
                        )
                      } else if (state.intervalUnit === 'day') {
                        if (state.interval === 1) {
                          desc = <>Every day at {timeNode}</>
                        } else {
                          desc = (
                            <>
                              Every{' '}
                              <span className="font-semibold text-gray-900">{state.interval}</span>{' '}
                              days at {timeNode}
                            </>
                          )
                        }
                      } else if (state.intervalUnit === 'week') {
                        desc = (
                          <>
                            Every{' '}
                            {state.interval > 1 && (
                              <span className="font-semibold text-gray-900">{state.interval} </span>
                            )}
                            {state.interval > 1 ? 'weeks' : 'week'} on{' '}
                            <span className="font-semibold text-gray-900">
                              {state.daysOfWeek.length > 0
                                ? state.daysOfWeek.map((d) => weekDays[d]).join(', ')
                                : weekDays[0]}
                            </span>{' '}
                            at {timeNode}
                          </>
                        )
                      } else if (state.intervalUnit === 'month' && state.monthWeekdayRule) {
                        const ordinals = ['', '1st', '2nd', '3rd', '4th', 'last']
                        desc = (
                          <>
                            {state.interval > 1 && (
                              <>
                                Every{' '}
                                <span className="font-semibold text-gray-900">
                                  {state.interval}
                                </span>{' '}
                                months,{' '}
                              </>
                            )}
                            On the{' '}
                            <span className="font-semibold text-gray-900">
                              {ordinals[state.monthWeekdayRule.which]}{' '}
                              {weekDays[state.monthWeekdayRule.weekday]}
                            </span>{' '}
                            at {timeNode}
                          </>
                        )
                      } else if (state.intervalUnit === 'month') {
                        const day = state.monthDayOfMonth ?? 1
                        desc = (
                          <>
                            {state.interval > 1 && (
                              <>
                                Every{' '}
                                <span className="font-semibold text-gray-900">
                                  {state.interval}
                                </span>{' '}
                                months,{' '}
                              </>
                            )}
                            On the{' '}
                            <span className="font-semibold text-gray-900">
                              {day}
                              {getOrdinalSuffix(day)}
                            </span>{' '}
                            at {timeNode}
                          </>
                        )
                      } else {
                        desc = 'Once'
                      }

                      return (
                        <>
                          <div>{desc}</div>
                          {browserNote}
                        </>
                      )
                    })()
                  : task.nextRunAt
                    ? (() => {
                        const tz = task.timezone ?? getUserTimezone()
                        const dt = new Date(task.nextRunAt)
                        const dateLabel = dt.toLocaleString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          timeZone: tz,
                        })
                        const timeLabel = dt.toLocaleString('en-US', {
                          hour: 'numeric',
                          minute: '2-digit',
                          timeZone: tz,
                        })
                        const tzAbbr = getShortTimezoneAbbr(tz)

                        const browserTz = getUserTimezone()
                        let browserNote: React.ReactNode = null
                        if (tz !== browserTz) {
                          const browserDate = dt.toLocaleString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            timeZone: browserTz,
                          })
                          const browserTime = dt.toLocaleString('en-US', {
                            hour: 'numeric',
                            minute: '2-digit',
                            timeZone: browserTz,
                          })
                          const browserAbbr = getShortTimezoneAbbr(browserTz)
                          browserNote = (
                            <div className="mt-0.5 text-xs text-gray-400">
                              {browserDate !== dateLabel ? `${browserDate} ` : ''}
                              {browserTime} {browserAbbr} your time
                            </div>
                          )
                        }

                        return (
                          <>
                            <div>
                              Runs once on{' '}
                              <span className="font-semibold text-gray-900">{dateLabel}</span> at{' '}
                              <span className="font-semibold text-gray-900">
                                {timeLabel} {tzAbbr}
                              </span>
                            </div>
                            {browserNote}
                          </>
                        )
                      })()
                    : 'Runs once'}
                {task.maxRuns && (
                  <span className="ml-2 text-xs text-gray-500">(max {task.maxRuns} times)</span>
                )}
              </div>
            )}
          </div>

          {/* Notifications Section */}
          <div className="mb-6 rounded-2xl border border-gray-200 bg-gray-50 p-4">
            <div className="flex items-center gap-2">
              <Bell size={16} className="text-gray-600" />
              <h3 className="text-sm font-medium text-gray-700">Notifications</h3>
            </div>
            <div className="mt-3 flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-700">Email on completion</p>
                <p className="text-xs text-gray-500">Sends to your account email</p>
              </div>
              <button
                onClick={() =>
                  void onUpdate(task.id, {
                    notificationSettings: {
                      email: { enabled: !(task.notificationSettings?.email?.enabled ?? false) },
                    },
                  })
                }
                className={cn(
                  'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none',
                  task.notificationSettings?.email?.enabled ? 'bg-blue-500' : 'bg-gray-200'
                )}
                role="switch"
                aria-checked={task.notificationSettings?.email?.enabled ?? false}
              >
                <span
                  className={cn(
                    'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                    task.notificationSettings?.email?.enabled ? 'translate-x-5' : 'translate-x-0'
                  )}
                />
              </button>
            </div>
          </div>

          {/* Run Results Section */}
          <div className="mb-6 rounded-2xl border border-gray-200 bg-gray-50 p-4">
            <button
              onClick={() => setShowResults(!showResults)}
              className="flex w-full items-center justify-between"
            >
              <div className="flex items-center gap-2">
                <Info size={16} className="text-gray-600" />
                <h3 className="text-sm font-medium text-gray-700">Run Results</h3>
                {resultsData?.pagination?.total !== undefined && (
                  <span className="rounded-full bg-gray-200 px-2 py-0.5 text-xs text-gray-600">
                    {resultsData.pagination.total}
                  </span>
                )}
                {resultsData && resultsData.results.filter((r) => !r.seenAt).length > 0 && (
                  <span className="rounded-full bg-blue-500 px-2 py-0.5 text-xs font-medium text-white">
                    {resultsData.results.filter((r) => !r.seenAt).length} new
                  </span>
                )}
              </div>
              {showResults ? (
                <ChevronUp size={16} className="text-gray-500" />
              ) : (
                <ChevronDown size={16} className="text-gray-500" />
              )}
            </button>

            <AnimatePresence>
              {showResults && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="min-w-0 overflow-hidden"
                >
                  <div className="mt-4 min-w-0 space-y-2">
                    {isLoadingResults ? (
                      <div className="flex items-center justify-center py-4">
                        <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                      </div>
                    ) : !resultsData?.results?.length ? (
                      <div className="py-4 text-center text-sm text-gray-500">
                        No run results yet
                      </div>
                    ) : (
                      (() => {
                        const grouped = resultsData.results.reduce(
                          (acc, result) => {
                            if (!acc[result.executionId]) acc[result.executionId] = []
                            acc[result.executionId].push(result)
                            return acc
                          },
                          {} as Record<string, ScheduledTaskToolResult[]>
                        )

                        return Object.entries(grouped).map(([executionId, execResults]) => {
                          const completedCount = execResults.filter(
                            (r) => r.status === 'COMPLETED'
                          ).length
                          const failedCount = execResults.filter(
                            (r) => r.status === 'FAILED'
                          ).length
                          const firstResult = execResults[0]

                          return (
                            <div
                              key={executionId}
                              className="rounded-xl border border-gray-200 bg-white"
                            >
                              <div className="border-b border-gray-100 bg-gray-50 px-3 py-2">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs font-medium text-gray-700">
                                      {execResults.length}{' '}
                                      {execResults.length === 1 ? 'tool' : 'tools'} executed
                                    </span>
                                    {completedCount > 0 && (
                                      <span className="flex items-center gap-1 text-xs text-green-600">
                                        <CheckCircle2 className="h-3 w-3" /> {completedCount}
                                      </span>
                                    )}
                                    {failedCount > 0 && (
                                      <span className="flex items-center gap-1 text-xs text-red-600">
                                        <XCircle className="h-3 w-3" /> {failedCount}
                                      </span>
                                    )}
                                    <span className="text-xs text-gray-400">
                                      {formatDate(firstResult.startedAt, task.timezone)}
                                    </span>
                                  </div>
                                  <button
                                    onClick={() => handleOpenExecutionInCopilot(execResults)}
                                    disabled={creatingSessionForId === executionId}
                                    className={cn(
                                      'flex items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100',
                                      creatingSessionForId === executionId &&
                                        'cursor-wait opacity-50'
                                    )}
                                  >
                                    {creatingSessionForId === executionId ? (
                                      <Loader2 size={12} className="animate-spin" />
                                    ) : (
                                      <MessageCircle size={12} />
                                    )}
                                    Open in Copilot
                                  </button>
                                </div>
                              </div>
                              <div className="space-y-2 p-3">
                                {execResults.map((result) => (
                                  <div
                                    key={result.id}
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => {
                                      setResultModalResult(result)
                                      if (!result.seenAt) {
                                        markSeen({ taskId, resultIds: [result.id] })
                                      }
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault()
                                        setResultModalResult(result)
                                        if (!result.seenAt) {
                                          markSeen({ taskId, resultIds: [result.id] })
                                        }
                                      }
                                    }}
                                    className={cn(
                                      'flex w-full cursor-pointer items-center justify-between rounded-xl border bg-white p-3 text-left transition-colors hover:bg-gray-50',
                                      result.seenAt
                                        ? 'border-gray-200'
                                        : 'border-blue-200 bg-blue-50/30'
                                    )}
                                  >
                                    <div className="flex items-center gap-2">
                                      <ResultStatusIcon status={result.status} />
                                      <span className="text-gray-500 [&>svg]:h-3.5 [&>svg]:w-3.5">
                                        {getToolDisplay(result.toolName).icon}
                                      </span>
                                      <span className="text-sm font-medium text-gray-800">
                                        {getToolDisplay(result.toolName).displayName}
                                      </span>
                                      <span className="text-xs text-gray-500">
                                        {formatDuration(result.durationMs)}
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      {!result.seenAt && (
                                        <span className="h-2 w-2 flex-shrink-0 rounded-full bg-blue-500" />
                                      )}
                                      <span className="text-xs text-gray-400">
                                        {formatDate(result.startedAt, task.timezone)}
                                      </span>
                                      <ChevronDown size={14} className="text-gray-400" />
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )
                        })
                      })()
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <div className="flex items-center justify-between border-t border-gray-200 pt-6">
            <div className="flex gap-2">
              {/* Run Now button */}
              {task.status !== 'RUNNING' && (
                <button
                  onClick={async () => {
                    try {
                      await runNowTask(task.id).unwrap()
                      toast.success('Task queued for immediate execution')
                      setShowResults(true)
                    } catch {
                      toast.error('Failed to run task')
                    }
                  }}
                  disabled={isRunningNow}
                  className={cn(
                    'flex items-center gap-2 rounded-2xl bg-black px-4 py-2 text-sm font-medium text-white hover:bg-gray-800',
                    isRunningNow && 'cursor-wait opacity-50'
                  )}
                >
                  {isRunningNow ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <Play size={16} />
                  )}
                  Run Now
                </button>
              )}

              {task.status === 'PENDING' || task.status === 'RUNNING' ? (
                <button
                  onClick={async () => {
                    try {
                      await updateTask({ id: task.id, input: { status: 'PAUSED' } }).unwrap()
                      toast.success('Task frozen')
                    } catch {
                      toast.error('Failed to freeze task')
                    }
                  }}
                  className="flex items-center gap-2 rounded-2xl border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  <Pause size={16} />
                  Freeze
                </button>
              ) : task.status === 'PAUSED' ? (
                <button
                  onClick={async () => {
                    try {
                      await updateTask({ id: task.id, input: { status: 'PENDING' } }).unwrap()
                      toast.success('Task unfrozen')
                    } catch {
                      toast.error('Failed to unfreeze task')
                    }
                  }}
                  className="flex items-center gap-2 rounded-2xl border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  <Play size={16} />
                  Unfreeze
                </button>
              ) : null}

              {task.status !== 'CANCELLED' && task.status !== 'COMPLETED' && (
                <button
                  onClick={async () => {
                    try {
                      await skipNextTask(task.id).unwrap()
                      toast.success('Skipped next run')
                    } catch {
                      toast.error('Failed to skip task')
                    }
                  }}
                  className="flex items-center gap-2 rounded-2xl border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  <X size={16} />
                  Skip next
                </button>
              )}
            </div>

            <div className="flex gap-2">
              <button
                onClick={handleDelete}
                className="flex items-center gap-2 rounded-2xl border border-red-200 bg-red-50 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-100"
              >
                <Trash2 size={16} />
                Delete
              </button>
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={!!resultModalResult}
        onClose={() => {
          setResultModalResult(null)
          setResultModalDebugMode(false)
        }}
        className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-3xl bg-white shadow-2xl"
      >
        <div className="flex flex-col overflow-hidden rounded-3xl bg-white">
          {resultModalResult && (
            <>
              <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
                <div className="flex items-center gap-2">
                  <ResultStatusIcon status={resultModalResult.status} />
                  <span className="text-gray-500 [&>svg]:h-4 [&>svg]:w-4">
                    {getToolDisplay(resultModalResult.toolName).icon}
                  </span>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {getToolDisplay(resultModalResult.toolName).displayName}
                  </h3>
                  <span className="text-sm text-gray-500">
                    {formatDate(resultModalResult.startedAt, task.timezone)}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setResultModalDebugMode((v) => !v)}
                    className={cn(
                      'flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-xs font-medium transition-colors',
                      resultModalDebugMode
                        ? 'bg-gray-200 text-gray-800'
                        : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
                    )}
                    title={resultModalDebugMode ? 'Hide raw JSON' : 'Show raw JSON (debug)'}
                  >
                    <Bug size={14} />
                    {resultModalDebugMode ? 'Debug on' : 'Debug'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setResultModalResult(null)}
                    className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                    aria-label="Close"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>
              <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-4">
                {resultModalResult.error && (
                  <div className="rounded-lg bg-red-50 p-4">
                    <p className="text-sm font-medium text-red-700">Error</p>
                    <p className="mt-1 text-sm text-red-600">{resultModalResult.error}</p>
                  </div>
                )}
                {resultModalResult.result &&
                  (() => {
                    const wfExecId = isWorkflowExecutionResult(resultModalResult.toolName)
                      ? extractWorkflowExecutionId(
                          resultModalResult.result as Record<string, unknown>
                        )
                      : null
                    const rawResultBlock = (
                      <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-4">
                        <p className="mb-2 text-sm font-medium text-amber-800">Result (raw JSON)</p>
                        <pre className="max-h-64 overflow-auto rounded bg-white p-3 text-xs text-gray-700">
                          {JSON.stringify(resultModalResult.result, null, 2)}
                        </pre>
                      </div>
                    )
                    if (resultModalDebugMode) {
                      return (
                        <div className="space-y-4">
                          {wfExecId && <WorkflowResultView executionId={wfExecId} />}
                          {rawResultBlock}
                        </div>
                      )
                    }
                    if (wfExecId) {
                      return <WorkflowResultView executionId={wfExecId} />
                    }
                    return (
                      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                        <p className="mb-2 text-sm font-medium text-gray-700">Result</p>
                        <ObjectDisplay data={resultModalResult.result} />
                      </div>
                    )
                  })()}
                {resultModalResult.toolArguments && (
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                    <p className="mb-2 text-sm font-medium text-gray-700">Arguments</p>
                    {resultModalDebugMode ? (
                      <pre className="max-h-48 overflow-auto rounded bg-white p-3 text-xs text-gray-700">
                        {JSON.stringify(resultModalResult.toolArguments, null, 2)}
                      </pre>
                    ) : (
                      <ObjectDisplay data={resultModalResult.toolArguments} />
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </Modal>
    </>
  )
}
